import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import {
  ActionTypeEvent,
  ActionTypeStatus,
  MessageVariantModal,
  ScanCode as UIFoundationScanCode,
  useResponsiveCurrentValue,
  Variant
} from '@dcxwam/dcx-wam-ui-foundations'
import { useCookies } from 'react-cookie'
import { GIGYA_COOKIE_UID } from '../../../../utils/constants'
import { CbxConfigType, PageConfig } from '../../../../utils/page-config'
import {
  codeStatusMap,
  extractCodeFromScan,
  mapStatusToEnum
} from '../../RedeemCodes/utils'
import {
  CbxClaimResponse,
  CodeStatusEvent,
  PageConfigModel,
  RedemptionStatus
} from '../../RedeemCodes/types'
import { SubmitRewardsCode } from './SubmitRewardsCode'
import { RewardsFormProps } from './types'
import useFetchData from '../../../../hooks/useFetchData'
import useCodeRedeemedNotifier from '../../../../hooks/useCodeRedeemedNotifier'
import usePointsBalance from '../../../../hooks/usePointsBalance'
import { useAuthToken } from '../../../../hooks/useAuthToken'

export const REQUEST_REDEMPTION_OPTIONS = {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json'
  }
}

function RewardsForm(props: Readonly<RewardsFormProps>) {
  const { isAbleToClaim, renderChildren } = props
  const [openScanCode, setOpenScanCode] = useState(false)
  const [openEnterCode, setOpenEnterCode] = useState(false)

  const pageConfig = PageConfig?.getConfig() as PageConfigModel
  const cbxConfig = PageConfig?.getConfig(
    PageConfig?.ConfigObjects?.CbxConfig
  ) as CbxConfigType | null
  const scanCodesConfig = pageConfig?.scanCodesConfig
  const uiLabels = scanCodesConfig?.uiLabels
  const uiLinks = scanCodesConfig?.uiLinks
  const responseLabels = scanCodesConfig?.responseLabels

  const rewardRedemptionApi = scanCodesConfig?.api

  const {
    enterCodeLabel = 'input label',
    enterCodePlaceholder = 'placeholder',
    enterCodeHelper = 'helper text',
    enterCodeTooltipMessage = 'tooltip message',
    submitCodeButtonLabel = 'button',
    enterCodesModalTitle = 'Enter Codes'
  } = uiLabels ?? {}

  const {
    rewardsStorePath,
    contactUsPath,
    stillHaveQuestionsPath,
    enterManuallyPath
  } = uiLinks ?? {}

  const {
    clubCodeEntered = '',
    clubLimitError = '',
    clubMonthlyLimit = '',
    codeLimitError = '',
    codeRedeemedSuccessfully = '',
    firstCodeRedeemed = '',
    invalidCodeError = '',
    maxCodeLimit = '',
    superCodeRedeemed = '',
    systemError = ''
  } = responseLabels ?? {}

  const [inputValue, setInputValue] = useState<string>('')
  const [actualPointsBalance, setActualPointsBalance] = useState<
    number | undefined
  >()
  const [scanButtonTriggered, setScanButtonTriggered] = useState(false)
  const [actionTypeTriggered, setActionTypeTriggered] = useState<
    ActionTypeEvent | undefined
  >(undefined)
  const [statusResponse, setStatusResponse] = useState<
    CodeStatusEvent | undefined
  >(undefined)

  const [cookies] = useCookies([GIGYA_COOKIE_UID])
  const authToken = useAuthToken()
  const consumerId = (() => {
    try {
      return cookies?.gig_uid ? atob(cookies.gig_uid) : ''
    } catch {
      return ''
    }
  })()

  const { notifyCodeRedeemed } = useCodeRedeemedNotifier()
  const { setPointsBalance } = usePointsBalance()

  const handleReedemCodeSuccess = useCallback((json: CbxClaimResponse) => {
    const payload = json?.data ?? json
    const status = mapStatusToEnum(payload?.messageKey)
    setOpenEnterCode(false)
    setOpenScanCode(false)
    setStatusResponse({
      status,
      receivedAt: Date.now()
    })
    if (codeStatusMap[status] === 'success') {
      notifyCodeRedeemed(payload?.availablePointsBalance ?? undefined)
    }
    if (payload?.availablePointsBalance != null) {
      setActualPointsBalance(payload.availablePointsBalance)
    }
  }, [])

  const handleReedemCodeError = useCallback(() => {
    setOpenEnterCode(false)
    setOpenScanCode(false)
    setStatusResponse({
      status: RedemptionStatus.ServerError,
      receivedAt: Date.now()
    })
  }, [])

  const { isLoading, fetchData } = useFetchData<CbxClaimResponse>({
    url: rewardRedemptionApi ?? '',
    auto: false,
    options: {
      ...REQUEST_REDEMPTION_OPTIONS,
      headers: {
        ...REQUEST_REDEMPTION_OPTIONS.headers,
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        ...(cbxConfig?.xCountryCode
          ? { 'X-Country-Code': cbxConfig.xCountryCode }
          : {}),
        ...(cbxConfig?.xChannel ? { 'X-Channel': cbxConfig.xChannel } : {}),
        ...(cbxConfig?.brand ? { 'X-Brand': cbxConfig.brand } : {})
      }
    },
    onSuccess: handleReedemCodeSuccess,
    onError: handleReedemCodeError
  })

  const secondaryMessageVariantLink = useMemo(() => {
    if (!statusResponse) {
      return ''
    }
    if (codeStatusMap[statusResponse.status] === 'success') {
      return rewardsStorePath
    }
    return contactUsPath
  }, [statusResponse])

  useEffect(() => {
    if (actualPointsBalance && setPointsBalance) {
      setPointsBalance(actualPointsBalance)
    }
  }, [actualPointsBalance, setPointsBalance])

  const messageVariants = useMemo(
    () => ({
      [RedemptionStatus.InvalidCode]: invalidCodeError,
      [RedemptionStatus.SuperCodeSuccess]: superCodeRedeemed,
      [RedemptionStatus.FirstCodeSuccess]: firstCodeRedeemed,
      [RedemptionStatus.RegularSuccess]: codeRedeemedSuccessfully,
      [RedemptionStatus.RegularTenCodesLeftSuccess]: maxCodeLimit,
      [RedemptionStatus.RegularErrorExceed]: codeLimitError,
      [RedemptionStatus.Club36Success]: clubCodeEntered,
      [RedemptionStatus.Club36TenCodesLeftSuccess]: clubMonthlyLimit,
      [RedemptionStatus.Club36ErrorExceed]: clubLimitError,
      [RedemptionStatus.ServerError]: systemError
    }),
    [responseLabels]
  )

  const handleCodeInputChange = useCallback(({ target }: SyntheticEvent) => {
    const { value } = target as HTMLInputElement
    setInputValue(value)
  }, [])

  const handleSubmitCode = useCallback(
    (submitedCode: string) => {
      const parsedCode = extractCodeFromScan(submitedCode)
      fetchData(
        `${cbxConfig?.cbxUrl}${rewardRedemptionApi}/consumers/${consumerId}/codes/${parsedCode}/claim`
      )
    },
    [consumerId]
  )

  const handleSuccess = useCallback(() => {
    setInputValue('')
  }, [])

  const handleOpenScanCode = useCallback(() => {
    setOpenScanCode(true)
    setOpenEnterCode(false)
    setScanButtonTriggered(true)
  }, [])

  const handleOpenEnterCode = useCallback(() => {
    setOpenScanCode(false)
    setOpenEnterCode(true)
    setScanButtonTriggered(false)
  }, [])

  const responsiveTriggerFunction = useResponsiveCurrentValue([
    handleOpenScanCode,
    handleOpenScanCode,
    handleOpenEnterCode
  ])!

  const handleScanEnterCodeClick = useCallback(() => {
    setInputValue('')
    responsiveTriggerFunction()
  }, [])

  useEffect(() => {
    if (!actionTypeTriggered) {
      return
    }
    if (actionTypeTriggered.status === ActionTypeStatus.Scan) {
      handleOpenScanCode()
    } else {
      handleOpenEnterCode()
    }
  }, [actionTypeTriggered])

  if (!isAbleToClaim) {
    return renderChildren(handleScanEnterCodeClick)
  }

  return (
    <>
      <UIFoundationScanCode
        open={openScanCode}
        helpLinkUrl={stillHaveQuestionsPath}
        enterManuallyUrl={enterManuallyPath}
        onOpen={() => setScanButtonTriggered(true)}
        onClose={() => setOpenScanCode(false)}
        onScanCode={handleSubmitCode}
        onEnterManually={!enterManuallyPath ? handleOpenEnterCode : undefined}
      />
      <SubmitRewardsCode
        open={openEnterCode}
        inputValue={inputValue}
        isLoading={isLoading}
        enterCodeLabel={enterCodeLabel}
        enterCodePlaceholder={enterCodePlaceholder}
        enterCodeHelper={enterCodeHelper}
        enterCodeTooltipMessage={enterCodeTooltipMessage}
        submitCodeButtonLabel={submitCodeButtonLabel}
        enterCodesModalTitle={enterCodesModalTitle}
        onChange={handleCodeInputChange}
        onClose={() => setOpenEnterCode(false)}
        onSubmit={handleSubmitCode}
      />
      {statusResponse !== undefined && (
        <MessageVariantModal
          codeStatusResponse={statusResponse}
          scanButtonTriggered={scanButtonTriggered}
          secondaryMessageVariantLink={secondaryMessageVariantLink}
          messageVariants={messageVariants}
          isLoadingRequest={isLoading}
          onSuccess={handleSuccess}
          onCloseMessageVariant={() => setStatusResponse(undefined)}
          setActionTypeTriggered={setActionTypeTriggered}
          variant={
            codeStatusMap[statusResponse.status] === 'error'
              ? Variant.ERROR
              : Variant.SUCCESS
          }
        />
      )}
      {renderChildren(handleScanEnterCodeClick)}
    </>
  )
}

export { RewardsForm }
